import React, { useCallback, useEffect, useState } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Loader2, X, Download, Info, ExternalLink, Zap, Copy, Check, Key, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';
import api, { bookingKoalaSyncAPI } from '../../services/api';
import { Link, useNavigate } from 'react-router-dom';

const BK_ZAP_EVENTS = [
  { key: 'new', trigger: 'New Booking' },
  { key: 'updated', trigger: 'Booking Updated' },
  { key: 'completed', trigger: 'Booking Completed' },
  { key: 'cancelled', trigger: 'Booking Cancelled' },
  { key: 'deleted', trigger: 'Booking Deleted' },
];

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="p-1.5 rounded text-[var(--sf-text-muted)] hover:text-[var(--sf-text-primary)] hover:bg-[var(--sf-bg-page)]"
      title="Copy"
    >
      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

// Stage pill palette for the four lifecycle states returned by
// GET /integrations/bookingkoala/setup-progress.
const STAGE_PILL = {
  not_configured: { label: 'Not connected',  tone: 'bg-gray-100 text-[var(--sf-text-secondary)]' },
  configured:     { label: 'Awaiting first booking', tone: 'bg-yellow-100 text-yellow-800' },
  tested:         { label: 'Partial sync',   tone: 'bg-blue-100 text-blue-700' },
  active:         { label: 'Connected',      tone: 'bg-green-100 text-green-700' },
};

// Zap Templates — IDs filled in after they're authored in the Zapier
// Developer Platform. Until each ID is set, the row renders as a muted
// "coming soon" marker so the user understands the Connect flow is being
// finished rather than broken. Each entry is one of the 5 BK lifecycle
// events the SF backend already accepts (see bookingkoala-zapier.js).
const ZAP_TEMPLATES = [
  { eventType: 'new',       label: 'New Booking',       critical: true,  templateId: null },
  { eventType: 'updated',   label: 'Booking Updated',   critical: true,  templateId: null },
  { eventType: 'completed', label: 'Booking Completed', critical: false, templateId: null },
  { eventType: 'cancelled', label: 'Booking Cancelled', critical: false, templateId: null },
  { eventType: 'deleted',   label: 'Booking Deleted',   critical: false, templateId: null },
];

// Zapier Workflow Element — populated after:
//   1. The SF Zapier app is promoted to Beta (developer.zapier.com)
//   2. The 5 Zap Templates above are published and reviewed by Zapier
//   3. SF's domains are allowlisted (iframes blocked by default)
//   4. The embed HTML snippet is copied from Zapier's "Generate Code" tool
// Until then, EMBED_READY is false and the UI shows the fallback
// Advanced setup section below.
const ZAPIER_EMBED_READY = false;

const BookingKoalaLiveSync = () => {
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [error, setError] = useState('');
  const [showConnect, setShowConnect] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const apiBase = (process.env.REACT_APP_API_URL || api.defaults?.baseURL || '').replace(/\/$/, '');
  const hookUrl = (event) => `${apiBase}/integrations/bookingkoala/zapier/bookings/${event}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, progressRes] = await Promise.all([
        bookingKoalaSyncAPI.getStatus(),
        bookingKoalaSyncAPI.getSetupProgress().catch(() => null),
      ]);
      setStatus(statusRes);
      setProgress(progressRes);
      setError('');
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || 'Could not load live sync status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (status?.connected && !window.confirm('Generate a new key? The current key stops working immediately — update all 5 Zaps with the new one.')) return;
    setBusy(true);
    try {
      const res = await bookingKoalaSyncAPI.generateKey();
      setNewKey(res.apiKey);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not generate a key');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Disconnect live sync? Zaps using the current key will start failing.')) return;
    setBusy(true);
    try {
      await bookingKoalaSyncAPI.revokeKey();
      setNewKey(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not revoke the key');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-[var(--sf-border-light)] shadow-sm p-6 mb-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Zap className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[var(--sf-text-primary)]">Live sync via Zapier</h2>
            <p className="text-sm text-[var(--sf-text-secondary)]">
              New, updated, completed, cancelled and deleted Booking Koala bookings flow into ServiceFlow automatically.
            </p>
          </div>
        </div>
        {!loading && (() => {
          const stage = progress?.stage || (status?.connected ? 'configured' : 'not_configured');
          const pill = STAGE_PILL[stage] || STAGE_PILL.not_configured;
          return (
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${pill.tone}`}>
              {pill.label}
            </span>
          );
        })()}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--sf-text-secondary)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* ─── One-click Connect ─────────────────────────────────────────
              The primary CTA. When ZAPIER_EMBED_READY is true (post-Beta +
              templates + embed snippet pasted) this opens the embedded
              Workflow Element. Until then it opens an inline panel that
              explains setup is in progress and offers the Advanced manual
              setup as a fallback. ──────────────────────────────────────── */}
          <div className="mb-5 p-4 border border-[var(--sf-border-light)] rounded-xl bg-gradient-to-br from-orange-50/60 to-white">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <h3 className="text-base font-semibold text-[var(--sf-text-primary)] mb-1">
                  Connect BookingKoala in one click
                </h3>
                <p className="text-sm text-[var(--sf-text-secondary)]">
                  Zapier sets up all 5 booking flows with field mappings already in place — no manual configuration.
                </p>
              </div>
              <button
                onClick={() => setShowConnect((v) => !v)}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-60 text-sm font-medium whitespace-nowrap"
              >
                <Zap className="w-4 h-4" />
                {progress?.stage === 'active'
                  ? 'Manage connection'
                  : progress?.stage === 'tested' || progress?.stage === 'configured'
                  ? 'Continue setup'
                  : 'Connect BookingKoala'}
              </button>
            </div>

            {/* Setup progress strip — one row per Zap Template, lights green
                when GET /setup-progress reports that eventType has fired. */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-2">
              {ZAP_TEMPLATES.map((t) => {
                const fired = progress?.events?.[t.eventType]?.fired;
                return (
                  <div
                    key={t.eventType}
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs ${
                      fired
                        ? 'border-green-200 bg-green-50 text-green-800'
                        : 'border-[var(--sf-border-light)] bg-white text-[var(--sf-text-muted)]'
                    }`}
                  >
                    {fired ? <CheckCircle className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border-2 border-current opacity-40" />}
                    <span className="flex-1 truncate">{t.label}</span>
                    {t.critical && !fired && <span className="text-[10px] font-semibold uppercase opacity-60">req</span>}
                  </div>
                );
              })}
            </div>

            {showConnect && (
              <div className="mt-4 pt-4 border-t border-[var(--sf-border-light)]">
                {ZAPIER_EMBED_READY ? (
                  // ── Zapier Workflow Element mount point ──────────────────
                  // When the embed is ready, the Zapier-generated snippet
                  // (custom element tag + attributes) goes here. Zapier's
                  // "Generate Code" tool produces this snippet per-app.
                  <div id="zapier-workflow-element-mount" className="min-h-[480px]">
                    {/* <zapier-workflow sign-up-email="..." zap-templates="..."></zapier-workflow> */}
                  </div>
                ) : (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-[var(--sf-text-primary)]">
                    <p className="font-medium mb-2">Embedded Zapier onboarding is almost ready.</p>
                    <p className="text-[var(--sf-text-secondary)] mb-3">
                      The one-click flow activates once Zapier approves the ServiceFlow app for Beta and the 5 Zap
                      Templates are published. Until then, you can still connect using the Advanced setup below.
                    </p>
                    <button
                      onClick={() => { setShowConnect(false); setShowAdvanced(true); }}
                      className="text-sm font-medium text-blue-700 hover:text-blue-800 underline"
                    >
                      Use Advanced setup instead →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ─── Advanced setup (manual Generate Key + webhook URLs) ─────── */}
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            className="flex items-center gap-2 text-xs font-medium text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)] mb-3"
          >
            {showAdvanced ? '▾' : '▸'} Advanced setup (manual Zapier webhooks)
          </button>

          {showAdvanced && (
          <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <button
              onClick={generate}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-60 text-sm font-medium"
            >
              <Key className="w-4 h-4" /> {status?.connected ? 'Rotate API key' : 'Generate API key'}
            </button>
            {status?.connected && (
              <button
                onClick={revoke}
                disabled={busy}
                className="px-4 py-2 border border-[var(--sf-border-light)] rounded-lg text-sm text-[var(--sf-text-primary)] hover:bg-[var(--sf-bg-page)] disabled:opacity-60"
              >
                Disconnect
              </button>
            )}
            <button onClick={load} className="p-2 text-[var(--sf-text-muted)] hover:text-[var(--sf-text-primary)]" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            {status?.connected && (
              <span className="text-xs text-[var(--sf-text-muted)]">
                Key {status.keyPrefix}… · {status.lastUsedAt ? `last used ${new Date(status.lastUsedAt).toLocaleString()}` : 'not used yet'} · {status.syncedJobs} synced jobs
              </span>
            )}
          </div>

          {newKey && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm font-medium text-yellow-900 mb-2">Copy this key now — it won't be shown again.</p>
              <div className="flex items-center gap-2 bg-white border border-yellow-200 rounded px-3 py-2">
                <code className="text-sm break-all flex-1">{newKey}</code>
                <CopyButton value={newKey} />
              </div>
            </div>
          )}

          <div className="mb-4">
            <h3 className="text-sm font-semibold text-[var(--sf-text-primary)] mb-2">Set up one Zap per Booking Koala trigger</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-[var(--sf-text-primary)] mb-3">
              <li>Trigger: <strong>Booking Koala</strong> → the trigger listed below.</li>
              <li>Action: <strong>Webhooks by Zapier → POST</strong>, Payload Type <strong>JSON</strong>, URL from the table.</li>
              <li>Headers: <code className="text-xs bg-[var(--sf-bg-page)] px-1 rounded">Authorization</code> = <code className="text-xs bg-[var(--sf-bg-page)] px-1 rounded">Bearer &lt;your key&gt;</code>.</li>
              <li>Data: leave empty to send every booking field, or map the fields you want.</li>
            </ol>
            <div className="border border-[var(--sf-border-light)] rounded-lg divide-y divide-[var(--sf-border-light)]">
              {BK_ZAP_EVENTS.map((ev) => (
                <div key={ev.key} className="flex items-center gap-3 px-3 py-2">
                  <span className="w-40 flex-shrink-0 text-sm font-medium text-[var(--sf-text-primary)]">{ev.trigger}</span>
                  <code className="text-xs text-[var(--sf-text-secondary)] break-all flex-1">{hookUrl(ev.key)}</code>
                  <CopyButton value={hookUrl(ev.key)} />
                </div>
              ))}
            </div>
          </div>

          </>
          )}

          {/* ─── Recently synced bookings — always visible, independent of
                Advanced setup collapse because it's informational. ───────── */}
          {status?.recent?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[var(--sf-text-primary)] mb-2">Recently synced bookings</h3>
              <div className="border border-[var(--sf-border-light)] rounded-lg divide-y divide-[var(--sf-border-light)]">
                {status.recent.map((r) => (
                  <Link key={r.jobId} to={`/job/${r.jobId}`} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-[var(--sf-bg-page)]">
                    <span className="w-28 flex-shrink-0 text-[var(--sf-text-muted)]">BK #{r.bookingId}</span>
                    <span className="flex-1 truncate text-[var(--sf-text-primary)]">{r.serviceName || 'Booking'} · {r.scheduledDate}</span>
                    <span className="text-xs text-[var(--sf-text-secondary)] capitalize">{r.lastEvent}</span>
                    <span className="text-xs text-[var(--sf-text-muted)] capitalize w-20 text-right">{r.status}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const BookingKoalaIntegration = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Overview, 2: Upload, 3: Preview, 4: Import Settings, 5: Importing, 6: Results
  const [importType, setImportType] = useState(null); // 'customers' or 'jobs'
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importSettings, setImportSettings] = useState({
    updateExisting: false,
    skipDuplicates: true
  });
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState({
    customers: { current: 0, total: 0, imported: 0, skipped: 0, errors: 0 },
    jobs: { current: 0, total: 0, imported: 0, skipped: 0, errors: 0 }
  });

  // Booking Koala customer field mappings - based on actual customer export structure
  const customerFieldMappings = {
    'firstName': ['First Name', 'First name', 'first_name', 'firstName'],
    'lastName': ['Last Name', 'Last name', 'last_name', 'lastName'],
    'email': ['Email Address', 'Email', 'email', 'email_address'],
    'phone': ['Phone Number', 'Phone', 'phone', 'phone_number', 'mobile'],
    'additionalPhone': ['Additional Phone Number(s)', 'Additional Phone Number', 'additional_phone'],
    'address': ['Address', 'address', 'street_address'],
    'apt': ['Apt. No.', 'Apt', 'apt', 'Apt. No', 'apartment'],
    'city': ['City', 'city'],
    'state': ['State', 'state'],
    'zipCode': ['Zip/Postal Code', 'Zip/Postal code', 'Zip Code', 'zip_code', 'postal_code', 'Zip'],
    'companyName': ['Company Name', 'Company', 'company_name'],
    'notes': ['Note', 'Notes', 'notes', 'Private customer note'],
    'status': ['Status', 'status'],
    'tags': ['Tags', 'tags']
  };

  const jobFieldMappings = {
    'customerEmail': ['Email', 'email', 'Email Address'],
    'customerFirstName': ['First name', 'First Name', 'first_name', 'First name'],
    'customerLastName': ['Last name', 'Last Name', 'last_name', 'Last name'],
    'phone': ['Phone', 'phone', 'Phone Number', 'phone_number'],
    'address': ['Address', 'address', 'serviceAddress'],
    'apt': ['Apt', 'apt', 'Apt.', 'Apt. No.', 'apartment'],
    'city': ['City', 'city'],
    'state': ['State', 'state'],
    'zipCode': ['Zip/Postal code', 'Zip/Postal code', 'Zip Code', 'zip_code', 'Zip/Postal Code'],
    'companyName': ['Company name', 'Company Name', 'company_name'],
    'serviceName': ['Service', 'service', 'Service Name', 'service_name'],
    'scheduledDate': ['Date', 'date', 'Booking start date time', 'Booking start date time'],
    'scheduledTime': ['Time', 'time', 'Booking start date time', 'Booking end date time'],
    'bookingStartDateTime': ['Booking start date time', 'Booking start date time'],
    'bookingEndDateTime': ['Booking end date time', 'Booking end date time'],
    'status': ['Booking status', 'Booking status', 'Status', 'status'],
    'price': ['Final amount (USD)', 'Final amount (USD)', 'Service total (USD)', 'Service total (USD)', 'Price', 'price', 'Amount', 'amount'],
    'serviceTotal': ['Service total (USD)', 'Service total (USD)'],
    'finalAmount': ['Final amount (USD)', 'Final amount (USD)'],
    'address': ['Address', 'address'],
    'city': ['City', 'city'],
    'state': ['State', 'state'],
    'zipCode': ['Zip/Postal code', 'Zip/Postal code', 'Zip Code', 'zip_code'],
    'apt': ['Apt', 'apt', 'Apt.'],
    'notes': ['Booking note', 'Booking note', 'Private customer note', 'Provider note', 'Special notes', 'Notes', 'notes'],
    'bookingNote': ['Booking note', 'Booking note'],
    'providerNote': ['Provider note', 'Provider note'],
    'specialNotes': ['Special notes', 'Special notes'],
    'duration': ['Estimated job length (HH:MM)', 'Estimated job length (HH:MM)', 'Duration', 'duration'],
    'isRecurring': ['Frequency', 'frequency', 'Is Recurring', 'is_recurring'],
    'recurringFrequency': ['Frequency', 'frequency', 'Recurring Frequency', 'recurring_frequency'],
    'extras': ['Extras', 'extras'],
    'excludes': ['Excludes', 'excludes'],
    'assignedCrewExternalId': ['Provider details', 'Provider/team', 'assignedCrewExternalId'],
    'assignedCrewIds': ['Provider details', 'Provider/team', 'assignedCrewIds'],
    'serviceRegionExternalId': ['Location', 'Location id', 'serviceRegionExternalId'],
    'amountPaidByCustomer': ['Amount paid by customer (USD)', 'Amount paid by customer (USD)', 'amountPaidByCustomer'],
    'amountOwed': ['Amount owed by customer (USD)', 'Amount owed by customer (USD)', 'amountOwed'],
    'finalAmount': ['Final amount (USD)', 'Final amount (USD)', 'finalAmount'],
    'paymentMethod': ['Payment method', 'Payment method', 'paymentMethod', 'payment_method']
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(file.type) &&
          !file.name.endsWith('.csv') && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setError('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
        return;
      }
      setSelectedFile(file);
      setError('');
      parseFile(file);
    }
  };

  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        let workbook;
        
        if (file.name.endsWith('.csv')) {
          const text = data;
          // Proper CSV parsing that handles multi-line quoted fields
          // This is critical because Booking Koala exports have JSON in Provider details field that spans multiple lines
          const parseCSV = (csvText) => {
            const rows = [];
            let currentRow = [];
            let currentField = '';
            let inQuotes = false;
            let i = 0;
            
            while (i < csvText.length) {
              const char = csvText[i];
              const nextChar = i < csvText.length - 1 ? csvText[i + 1] : null;
              
              if (char === '"') {
                if (inQuotes && nextChar === '"') {
                  // Escaped quote (double quote inside quoted field) - add single quote to field
                  currentField += '"';
                  i += 2;
                } else {
                  // Toggle quote state (start or end of quoted field)
                  inQuotes = !inQuotes;
                  i++;
                }
              } else if (char === ',' && !inQuotes) {
                // Field separator (outside quotes)
                currentRow.push(currentField);
                currentField = '';
                i++;
              } else if ((char === '\n' || (char === '\r' && nextChar === '\n')) && !inQuotes) {
                // End of row (outside quotes)
                currentRow.push(currentField);
                if (currentRow.length > 0 && currentRow.some(f => f.trim() !== '')) {
                  rows.push(currentRow);
                }
                currentRow = [];
                currentField = '';
                if (char === '\r' && nextChar === '\n') {
                  i += 2; // Skip both \r and \n
                } else {
                  i++;
                }
              } else {
                // Regular character - add to current field
                currentField += char;
                i++;
              }
            }
            
            // Handle last field and row (if file doesn't end with newline)
            if (currentField !== '' || currentRow.length > 0) {
              currentRow.push(currentField);
            }
            if (currentRow.length > 0 && currentRow.some(f => f.trim() !== '')) {
              rows.push(currentRow);
            }
            
            return rows;
          };
          
          const parsedRows = parseCSV(text);
          if (parsedRows.length === 0) {
            setError('CSV file appears to be empty');
            return;
          }
          
          const headers = parsedRows[0].map(h => h.replace(/^"|"$/g, '').trim());
          const rows = parsedRows.slice(1).map(row => {
            const obj = {};
            headers.forEach((header, i) => {
              const value = (row[i] || '').replace(/^"|"$/g, '').trim();
              obj[header] = value;
            });
            return obj;
          });
          
          setFileData(rows);
          // Set preview data as raw for now, will be mapped in the preview step
          setPreviewData(rows.slice(0, 10));
        } else {
          workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          setFileData(jsonData);
          // Set preview data as raw for now, will be mapped in the preview step
          setPreviewData(jsonData.slice(0, 10));
        }
        setStep(3); // Go to preview step
      } catch (err) {
        console.error('Error parsing file:', err);
        setError('Failed to parse file. Please ensure it is a valid CSV or Excel file.');
      }
    };
    reader.onerror = () => {
      setError('Failed to read file. Please try again.');
    };
    
    if (file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  const parseDateTime = (dateTimeStr, dateStr, timeStr) => {
    // Try Booking start date time first (ISO format)
    if (dateTimeStr) {
      try {
        const dt = new Date(dateTimeStr);
        if (!isNaN(dt.getTime())) {
          return {
            date: dt.toISOString().split('T')[0],
            time: dt.toTimeString().split(' ')[0].substring(0, 5)
          };
        }
      } catch (e) {
        // Continue to other methods
      }
    }
    
    // Try Date and Time fields separately
    if (dateStr && timeStr) {
      try {
        // Handle date formats like "10/02/2025"
        const dateParts = dateStr.split('/');
        if (dateParts.length === 3) {
          const month = dateParts[0].padStart(2, '0');
          const day = dateParts[1].padStart(2, '0');
          const year = dateParts[2];
          const date = `${year}-${month}-${day}`;
          
          // Handle time formats like "09:00 AM"
          let time = timeStr;
          if (timeStr.includes('AM') || timeStr.includes('PM')) {
            const [timePart, period] = timeStr.split(' ');
            const [hours, minutes] = timePart.split(':');
            let hour24 = parseInt(hours);
            if (period === 'PM' && hour24 !== 12) hour24 += 12;
            if (period === 'AM' && hour24 === 12) hour24 = 0;
            time = `${hour24.toString().padStart(2, '0')}:${minutes}`;
          }
          
          return { date, time };
        }
      } catch (e) {
        // Continue
      }
    }
    
    return { date: null, time: null };
  };

  const parseDuration = (durationStr) => {
    // Handle "HH:MM" format like "05:45"
    if (durationStr && durationStr.includes(':')) {
      const [hours, minutes] = durationStr.split(':');
      return (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
    }
    return null;
  };

  const parseRecurring = (frequencyStr) => {
    if (!frequencyStr) return { isRecurring: false, frequency: null };
    
    const freq = frequencyStr.toLowerCase();
    if (freq === 'one-time' || freq === 'onetime') {
      return { isRecurring: false, frequency: null };
    }
    
    // Map Booking Koala frequencies to our format
    const frequencyMap = {
      'weekly': 'weekly',
      'every other week': 'bi-weekly',
      'every 4 weeks': 'monthly',
      'every 2 weeks': 'bi-weekly'
    };
    
    for (const [key, value] of Object.entries(frequencyMap)) {
      if (freq.includes(key)) {
        return { isRecurring: true, frequency: value };
      }
    }
    
    return { isRecurring: true, frequency: 'custom' };
  };

  const mapFields = (data, type) => {
    const mappings = type === 'customers' ? customerFieldMappings : jobFieldMappings;
    return data.map(row => {
      const mapped = {};
      Object.keys(mappings).forEach(key => {
        const possibleNames = mappings[key];
        for (const name of possibleNames) {
          if (row[name] !== undefined && row[name] !== null && row[name] !== '') {
            const value = row[name];
            
            // Special handling for specific fields
            if (type === 'jobs') {
              if (key === 'scheduledDate' || key === 'scheduledTime') {
                const dt = parseDateTime(
                  row['Booking start date time'],
                  row['Date'],
                  row['Time']
                );
                if (key === 'scheduledDate') mapped['scheduledDate'] = dt.date;
                if (key === 'scheduledTime') mapped['scheduledTime'] = dt.time;
                continue;
              }
              
              if (key === 'duration') {
                const duration = parseDuration(value);
                if (duration) mapped[key] = duration;
                continue;
              }
              
              if (key === 'isRecurring' || key === 'recurringFrequency') {
                const recurring = parseRecurring(row['Frequency'] || value);
                if (key === 'isRecurring') mapped['isRecurring'] = recurring.isRecurring;
                if (key === 'recurringFrequency') mapped['recurringFrequency'] = recurring.frequency;
                continue;
              }
              
              // Combine multiple note fields
              if (key === 'notes') {
                const notes = [
                  row['Booking note'],
                  row['Private customer note'],
                  row['Provider note'],
                  row['Special notes']
                ].filter(n => n && n.trim()).join('\n\n');
                if (notes) mapped[key] = notes;
                continue;
              }
              
              // Map Booking status to our status
              if (key === 'status') {
                const statusMap = {
                  'Completed': 'completed',
                  'Upcoming': 'pending',
                  'Unassigned': 'pending',
                  'Cancelled': 'cancelled'
                };
                mapped[key] = statusMap[value] || value.toLowerCase() || 'pending';
                continue;
              }
              
              // Extract provider/team member info from Provider details JSON
              if (key === 'assignedCrewExternalId' || key === 'assignedCrewIds') {
                const providerDetails = row['Provider details'];
                if (providerDetails) {
                  try {
                    // Provider details is a non-standard JSON string like: "[{Id: 16, Name: 'Andrii Polovyi', ...}]"
                    // First, try to convert it to valid JSON by replacing unquoted keys
                    let jsonStr = providerDetails.trim();
                    if (jsonStr.startsWith('[') && jsonStr.endsWith(']')) {
                      // Replace unquoted keys with quoted keys
                      jsonStr = jsonStr.replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":');
                      // Replace single quotes with double quotes for string values
                      jsonStr = jsonStr.replace(/'/g, '"');
                      
                      const providers = JSON.parse(jsonStr);
                      if (Array.isArray(providers) && providers.length > 0) {
                        const providerIds = providers.map(p => {
                          // Try different ID field names
                          return p.Id || p.id || p['Id'] || p['Email Id'] || p['EmailId'];
                        }).filter(id => id);
                        
                        if (providerIds.length > 0) {
                          mapped['assignedCrewExternalId'] = providerIds[0].toString(); // First provider as primary
                          mapped['assignedCrewIds'] = providerIds.map(id => id.toString()); // All providers
                        }
                      }
                    }
                  } catch (e) {
                    console.warn('Failed to parse Provider details:', e, 'Raw value:', providerDetails);
                    // Fallback: try to extract IDs from Provider/team field which has format like "16: Andrii Polovyi" or "16: Andrii Polovyi, 22: Natalya Pankevich"
                    const providerTeam = row['Provider/team'] || row['Provider/team (without ids)'];
                    if (providerTeam) {
                      // Handle comma-separated providers: "16: Name1, 22: Name2"
                      const providerMatches = providerTeam.matchAll(/(\d+):/g);
                      const providerIds = [];
                      for (const match of providerMatches) {
                        if (match[1]) {
                          providerIds.push(match[1]);
                        }
                      }
                      
                      if (providerIds.length > 0) {
                        mapped['assignedCrewExternalId'] = providerIds[0].toString(); // First provider as primary
                        mapped['assignedCrewIds'] = providerIds.map(id => id.toString()); // All providers
                      } else {
                        // Fallback to single provider format
                        const singleMatch = providerTeam.match(/^(\d+):/);
                        if (singleMatch) {
                          mapped['assignedCrewExternalId'] = singleMatch[1];
                          mapped['assignedCrewIds'] = [singleMatch[1]];
                        }
                      }
                    }
                  }
                }
                continue;
              }
              
              // Extract location/territory info from Location field
              if (key === 'serviceRegionExternalId') {
                const location = row['Location'] || row['Location id'];
                if (location) {
                  // Use Location as external ID for territory creation
                  mapped['serviceRegionExternalId'] = location.toString();
                }
                continue;
              }
            }
            
            mapped[key] = value;
            break;
          }
        }
      });
      
      // For jobs, ensure we have date/time from Booking start date time if not already set
      if (type === 'jobs' && !mapped.scheduledDate && row['Booking start date time']) {
        const dt = parseDateTime(row['Booking start date time'], row['Date'], row['Time']);
        if (dt.date) mapped.scheduledDate = dt.date;
        if (dt.time) mapped.scheduledTime = dt.time;
      }
      
      // For jobs, also extract customer fields from the row (Booking Koala jobs CSV includes customer data)
      // This is critical - the jobs CSV has customer data embedded in each row
      // IMPORTANT: Always extract these fields to ensure backend can create customers on-the-fly
      if (type === 'jobs') {
        // Extract customer fields directly from CSV columns (try all possible variations)
        // CSV has: "First name", "Last name", "Email", "Phone", "Address", "Apt", "City", "State", "Zip/Postal code"
        // We MUST extract these even if empty, so backend knows they were attempted
        const firstName = row['First name'] || row['First Name'] || row['first name'] || row['firstName'] || row['first_name'] || '';
        const lastName = row['Last name'] || row['Last Name'] || row['last name'] || row['lastName'] || row['last_name'] || '';
        const email = row['Email'] || row['email'] || row['Email Address'] || row['email address'] || '';
        const phone = row['Phone'] || row['phone'] || row['Phone Number'] || row['phone number'] || '';
        const address = row['Address'] || row['address'] || '';
        const apt = row['Apt'] || row['apt'] || row['Apt. No.'] || row['Apt. No'] || row['apartment'] || '';
        const city = row['City'] || row['city'] || '';
        const state = row['State'] || row['state'] || '';
        const zipCode = row['Zip/Postal code'] || row['Zip/Postal Code'] || row['zip/postal code'] || row['Zip Code'] || row['zip code'] || '';
        const companyName = row['Company name'] || row['Company Name'] || row['company name'] || row['companyName'] || '';
        
        // ALWAYS set customer fields (even if empty) so backend receives them
        // Backend will validate and show appropriate errors if required fields are missing
        mapped['customerFirstName'] = firstName;
        mapped['customerLastName'] = lastName;
        mapped['customerEmail'] = email;
        if (phone) mapped['phone'] = phone;
        if (address) mapped['address'] = address;
        if (apt) mapped['apt'] = apt;
        if (city) mapped['city'] = city;
        if (state) mapped['state'] = state;
        if (zipCode) mapped['zipCode'] = zipCode;
        
        // ALSO preserve raw CSV column names as fallback (backend can use these directly)
        // This ensures backend can find fields even if mapped names don't work
        if (row['First name'] !== undefined) mapped['First name'] = row['First name'];
        if (row['Last name'] !== undefined) mapped['Last name'] = row['Last name'];
        if (row['Email'] !== undefined) mapped['Email'] = row['Email'];
        if (row['Phone'] !== undefined) mapped['Phone'] = row['Phone'];
        if (row['Address'] !== undefined) mapped['Address'] = row['Address'];
        if (row['Apt'] !== undefined) mapped['Apt'] = row['Apt'];
        if (row['City'] !== undefined) mapped['City'] = row['City'];
        if (row['State'] !== undefined) mapped['State'] = row['State'];
        if (row['Zip/Postal code'] !== undefined) mapped['Zip/Postal code'] = row['Zip/Postal code'];
        
        // Note: companyName not stored in customers table, but we extract it for completeness
      }
      
      return mapped;
    });
  };

  const handleImport = async () => {
    if (!fileData || fileData.length === 0) {
      setError('No data to import');
      return;
    }

    setIsImporting(true);
    setError('');
    setStep(5); // Importing step

    try {
      // Detect data type - Booking Koala has separate customer and job exports
      const keys = Object.keys(fileData[0] || {});
      
      // Check for customer-specific fields (from customer export)
      const hasCustomerOnlyFields = keys.some(k => {
        const lower = k.toLowerCase();
        return lower === 'email address' || 
               lower === 'phone number' ||
               lower === 'number of bookings' ||
               lower === 'number of active bookings' ||
               lower === 'referral code' ||
               lower === 'created on';
      });
      
      // Check for job-specific fields (from bookings export)
      const hasJobOnlyFields = keys.some(k => {
        const lower = k.toLowerCase();
        return lower.includes('booking start date time') ||
               lower.includes('booking end date time') ||
               lower.includes('booking status') ||
               lower.includes('final amount') ||
               lower.includes('service total') ||
               lower.includes('provider details') ||
               lower.includes('estimated job length');
      });
      
      // Check for general customer fields
      const hasCustomerFields = keys.some(k => {
        const lower = k.toLowerCase();
        return lower.includes('email') || 
               lower.includes('first name') || 
               lower.includes('last name') ||
               lower.includes('phone');
      });
      
      // Check for general job fields
      const hasJobFields = keys.some(k => {
        const lower = k.toLowerCase();
        return lower.includes('service') || 
               lower.includes('booking') ||
               lower.includes('date') ||
               lower.includes('time') ||
               lower.includes('status');
      });

      // Use the selected import type, or auto-detect if not set
      let dataType = importType || 'unknown';
      
      if (!importType) {
        // Auto-detect if import type wasn't selected
        if (hasCustomerOnlyFields) {
          dataType = 'customers';
        } else if (hasJobOnlyFields) {
          dataType = 'jobs';
        } else if (hasCustomerFields && hasJobFields) {
          dataType = 'both'; // Combined export
        } else if (hasJobFields) {
          dataType = 'jobs';
        } else if (hasCustomerFields) {
          dataType = 'customers';
        }
      }

      let customers = [];
      let jobs = [];

      // Process based on selected import type
      if (dataType === 'customers' || (dataType === 'both' && importType === 'customers')) {
        // Customer-only file - all rows are customers
        customers = mapFields(fileData, 'customers');
      } else if (dataType === 'jobs' || (dataType === 'both' && importType === 'jobs')) {
        // Job-only file - all rows are jobs
        jobs = mapFields(fileData, 'jobs');
      } else if (dataType === 'both' && !importType) {
        // Combined file - extract both (fallback if no type selected)
        const customerMap = new Map();
        fileData.forEach(row => {
          const email = (row['Email Address'] || row['Email'] || row.email || '').toLowerCase().trim();
          const firstName = row['First Name'] || row['First name'] || row.firstName || '';
          const lastName = row['Last Name'] || row['Last name'] || row.lastName || '';
          
          if (email || (firstName && lastName)) {
            const key = email || `${firstName} ${lastName}`.toLowerCase();
            if (!customerMap.has(key)) {
              customerMap.set(key, row);
            }
          }
        });
        customers = mapFields(Array.from(customerMap.values()), 'customers');
        jobs = mapFields(fileData, 'jobs');
      } else {
        throw new Error(`Unable to determine file type. Please ensure you've selected the correct import type and uploaded a valid ${importType || 'Booking Koala'} export file.`);
      }

      // Initialize progress
      setImportProgress({
        customers: { current: 0, total: customers.length, imported: 0, skipped: 0, errors: 0 },
        jobs: { current: 0, total: jobs.length, imported: 0, skipped: 0, errors: 0 }
      });

      // Use fetch API for streaming response with progress updates
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      // Get base URL - api.defaults.baseURL already includes '/api'
      let apiBaseUrl = process.env.REACT_APP_API_URL;
      if (!apiBaseUrl && api.defaults?.baseURL) {
        apiBaseUrl = api.defaults.baseURL;
      }
      if (!apiBaseUrl) {
        apiBaseUrl = 'http://localhost:3000/api';
      }
      
      // Remove trailing '/api' if present since we'll add the full path
      apiBaseUrl = apiBaseUrl.replace(/\/api$/, '');
      
      // Construct the full endpoint URL
      const endpointUrl = `${apiBaseUrl}/api/booking-koala/import`;
      console.log('📤 Booking Koala import URL:', endpointUrl);
      
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customers: customers.length > 0 ? customers : undefined,
          jobs: jobs.length > 0 ? jobs : undefined,
          importSettings
        })
      });

      if (!response.ok) {
        // Try to read error message
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Import failed');
        } catch (e) {
          throw new Error('Import failed');
        }
      }

      // Read streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.trim()) {
            try {
              const progress = JSON.parse(line);
              if (progress.type === 'progress') {
                setImportProgress({
                  customers: progress.customers || { current: 0, total: customers.length, imported: 0, skipped: 0, errors: 0 },
                  jobs: progress.jobs || { current: 0, total: jobs.length, imported: 0, skipped: 0, errors: 0 }
                });
              } else if (progress.type === 'complete') {
                setImportResult({
                  imported: (progress.results.customers.imported || 0) + (progress.results.jobs.imported || 0),
                  skipped: (progress.results.customers.skipped || 0) + (progress.results.jobs.skipped || 0),
                  errors: [
                    ...(progress.results.customers.errors || []),
                    ...(progress.results.jobs.errors || [])
                  ]
                });
                setStep(6); // Results step
              } else if (progress.type === 'error') {
                throw new Error(progress.error || progress.message || 'Import failed');
              }
            } catch (e) {
              // Ignore JSON parse errors for incomplete chunks
              if (e.message && e.message !== 'Unexpected end of JSON input') {
                console.error('Error parsing progress:', e);
              }
            }
          }
        }
      }
    } catch (err) {
      console.error('Import error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to import data');
      setStep(4);
    } finally {
      setIsImporting(false);
    }
  };

  const resetImport = () => {
    setStep(1);
    setImportType(null);
    setSelectedFile(null);
    setFileData(null);
    setPreviewData(null);
    setError('');
    setImportResult(null);
  };

  // Step 1: Overview/Introduction
  if (step === 1) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center space-x-2 text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)] mb-4"
          >
            <span>← Back to Settings</span>
          </button>
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Upload className="w-8 h-8 text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[var(--sf-text-primary)]">Booking Koala Integration</h1>
              <p className="text-[var(--sf-text-secondary)]">Sync bookings live from Booking Koala, or import customers and jobs from a CSV export</p>
            </div>
          </div>
        </div>

        <BookingKoalaLiveSync />

        <div className="bg-white rounded-lg border border-[var(--sf-border-light)] shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-[var(--sf-text-primary)] mb-4">About Booking Koala Import</h2>
          <p className="text-[var(--sf-text-primary)] mb-4">
            Easily migrate your data from Booking Koala to ZenBooker. Choose what you want to import:
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* Customer Import Card */}
            <div className="border-2 border-[var(--sf-border-light)] rounded-lg p-6 hover:border-orange-400 transition-colors">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-[var(--sf-blue-500)]" />
                </div>
                <h3 className="text-lg font-semibold text-[var(--sf-text-primary)]">Import Customers</h3>
              </div>
              <p className="text-sm text-[var(--sf-text-secondary)] mb-4">
                Import customer data from Booking Koala customer export. Includes:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-[var(--sf-text-primary)] mb-4">
                <li>Name, email, phone</li>
                <li>Address information</li>
                <li>Customer notes and tags</li>
              </ul>
              <button
                onClick={() => {
                  setImportType('customers');
                  setStep(2);
                }}
                className="w-full px-4 py-2 bg-[var(--sf-blue-500)] text-white rounded-lg hover:bg-[var(--sf-blue-600)] transition-colors font-medium"
              >
                Import Customers
              </button>
            </div>

            {/* Job Import Card */}
            <div className="border-2 border-[var(--sf-border-light)] rounded-lg p-6 hover:border-orange-400 transition-colors">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-[var(--sf-text-primary)]">Import Jobs</h3>
              </div>
              <p className="text-sm text-[var(--sf-text-secondary)] mb-4">
                Import booking/job data from Booking Koala bookings export. Includes:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-[var(--sf-text-primary)] mb-4">
                <li>Service details and scheduling</li>
                <li>Provider/team assignments</li>
                <li>Pricing and status</li>
                <li>Auto-creates team members & territories</li>
              </ul>
              <button
                onClick={() => {
                  setImportType('jobs');
                  setStep(2);
                }}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Import Jobs
              </button>
            </div>
          </div>

          <div className="bg-[var(--sf-blue-50)] border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Info className="w-5 h-5 text-[var(--sf-blue-500)] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">How to Export from Booking Koala</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  <li>Log in to your Booking Koala account</li>
                  <li>Navigate to Reports or Export section</li>
                  <li>Export your <strong>customers</strong> or <strong>bookings</strong> data as CSV or Excel</li>
                  <li>Download the file to your computer</li>
                  <li>Choose the appropriate import type above and upload the file</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-1">Supported File Formats</h3>
                <p className="text-sm text-yellow-800">
                  CSV (.csv), Excel (.xlsx, .xls)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: File Upload
  if (step === 2) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <button
            onClick={() => {
              setStep(1);
              setImportType(null);
            }}
            className="flex items-center space-x-2 text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)] mb-4"
          >
            <span>← Back</span>
          </button>
          <h1 className="text-2xl font-bold text-[var(--sf-text-primary)]">
            Upload Booking Koala {importType === 'customers' ? 'Customers' : 'Jobs'} File
          </h1>
          <p className="text-[var(--sf-text-secondary)] mt-2">
            {importType === 'customers' 
              ? 'Upload your Booking Koala customer export file'
              : 'Upload your Booking Koala bookings/jobs export file'}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-600">{error}</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border border-[var(--sf-border-light)] shadow-sm p-6">
          <div className="border-2 border-dashed border-[var(--sf-border-light)] rounded-lg p-8 text-center hover:border-orange-400 transition-colors">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex flex-col items-center"
            >
              <Upload className="w-12 h-12 text-[var(--sf-text-muted)] mb-4" />
              <p className="text-lg font-medium text-[var(--sf-text-primary)] mb-2">
                Click to upload or drag and drop
              </p>
              <p className="text-sm text-[var(--sf-text-muted)]">
                CSV or Excel files (.csv, .xlsx, .xls)
              </p>
            </label>
          </div>

          {selectedFile && (
            <div className="mt-4 p-4 bg-[var(--sf-bg-page)] rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-[var(--sf-text-secondary)]" />
                  <span className="text-sm font-medium text-[var(--sf-text-primary)]">{selectedFile.name}</span>
                  <span className="text-xs text-[var(--sf-text-muted)]">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </span>
                </div>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setFileData(null);
                    setPreviewData(null);
                  }}
                  className="text-[var(--sf-text-muted)] hover:text-[var(--sf-text-secondary)]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Step 3: Preview Data
  if (step === 3) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <button
            onClick={() => setStep(2)}
            className="flex items-center space-x-2 text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)] mb-4"
          >
            <span>← Back</span>
          </button>
          <h1 className="text-2xl font-bold text-[var(--sf-text-primary)]">
            Preview {importType === 'customers' ? 'Customers' : 'Jobs'} Data
          </h1>
          <p className="text-[var(--sf-text-secondary)]">Review the first 10 rows of your {importType === 'customers' ? 'customer' : 'job'} data</p>
        </div>

        {previewData && previewData.length > 0 && (() => {
          // Map the preview data to show the actual fields that will be imported
          const mappedPreview = mapFields(previewData, importType || 'jobs');
          const displayData = mappedPreview.slice(0, 10);
          
          if (displayData.length === 0 || !displayData[0]) {
            return (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800">No data could be mapped. Please check your file format.</p>
              </div>
            );
          }
          
          return (
            <div className="bg-white rounded-lg border border-[var(--sf-border-light)] shadow-sm overflow-hidden mb-6">
              <div className="p-4 bg-[var(--sf-blue-50)] border-b border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Preview of mapped data:</strong> This shows how your data will be imported. 
                  Only the first 10 rows are shown.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[var(--sf-border-light)]">
                  <thead className="bg-[var(--sf-bg-page)]">
                    <tr>
                      {Object.keys(displayData[0]).map((key) => (
                        <th
                          key={key}
                          className="px-4 py-3 text-left text-xs font-medium text-[var(--sf-text-muted)] uppercase tracking-wider"
                        >
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[var(--sf-border-light)]">
                    {displayData.map((row, idx) => (
                      <tr key={idx}>
                        {Object.keys(displayData[0]).map((key) => {
                          const value = row[key];
                          // Handle objects/arrays by stringifying them for display
                          const displayValue = typeof value === 'object' && value !== null 
                            ? JSON.stringify(value) 
                            : (value || '-');
                          return (
                            <td key={key} className="px-4 py-3 text-sm text-[var(--sf-text-primary)] max-w-xs truncate" title={typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}>
                              {String(displayValue).substring(0, 100)}{String(displayValue).length > 100 ? '...' : ''}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        <div className="flex justify-end space-x-4">
          <button
            onClick={() => setStep(2)}
            className="px-6 py-2 border border-[var(--sf-border-light)] rounded-lg text-[var(--sf-text-primary)] hover:bg-[var(--sf-bg-page)]"
          >
            Cancel
          </button>
          <button
            onClick={() => setStep(4)}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            Continue to Import Settings
          </button>
        </div>
      </div>
    );
  }

  // Step 4: Import Settings
  if (step === 4) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <button
            onClick={() => setStep(3)}
            className="flex items-center space-x-2 text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)] mb-4"
          >
            <span>← Back</span>
          </button>
          <h1 className="text-2xl font-bold text-[var(--sf-text-primary)]">Import Settings</h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-600">{error}</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border border-[var(--sf-border-light)] shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-[var(--sf-text-primary)] mb-4">Duplicate Handling</h2>
          
          <div className="space-y-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={importSettings.skipDuplicates}
                onChange={(e) => setImportSettings({ ...importSettings, skipDuplicates: e.target.checked })}
                className="mt-1"
              />
              <div>
                <span className="font-medium text-[var(--sf-text-primary)]">Skip Duplicates</span>
                <p className="text-sm text-[var(--sf-text-secondary)]">
                  Skip records that already exist in your system
                </p>
              </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={importSettings.updateExisting}
                onChange={(e) => setImportSettings({ ...importSettings, updateExisting: e.target.checked })}
                className="mt-1"
                disabled={!importSettings.skipDuplicates}
              />
              <div>
                <span className="font-medium text-[var(--sf-text-primary)]">Update Existing Records</span>
                <p className="text-sm text-[var(--sf-text-secondary)]">
                  Update existing records with new data instead of skipping them
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={() => setStep(3)}
            className="px-6 py-2 border border-[var(--sf-border-light)] rounded-lg text-[var(--sf-text-primary)] hover:bg-[var(--sf-bg-page)]"
          >
            Back
          </button>
          <button
            onClick={handleImport}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            Start Import
          </button>
        </div>
      </div>
    );
  }

  // Step 5: Importing
  if (step === 5) {
    const customersProgress = importProgress.customers.total > 0 
      ? (importProgress.customers.current / importProgress.customers.total) * 100 
      : 0;
    const jobsProgress = importProgress.jobs.total > 0 
      ? (importProgress.jobs.current / importProgress.jobs.total) * 100 
      : 0;
    const overallProgress = importProgress.customers.total + importProgress.jobs.total > 0
      ? ((importProgress.customers.current + importProgress.jobs.current) / (importProgress.customers.total + importProgress.jobs.total)) * 100
      : 0;

    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-8">
          <Loader2 className="w-16 h-16 text-orange-600 mx-auto mb-4 animate-spin" />
          <h1 className="text-2xl font-bold text-[var(--sf-text-primary)] mb-2">Importing Data...</h1>
          <p className="text-[var(--sf-text-secondary)] mb-6">Please wait while we import your Booking Koala data</p>
          
          {/* Overall Progress */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-[var(--sf-text-primary)]">Overall Progress</span>
              <span className="text-sm font-medium text-[var(--sf-text-primary)]">{Math.round(overallProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div 
                className="bg-orange-600 h-3 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
            <p className="text-xs text-[var(--sf-text-muted)] mt-1">
              {importProgress.customers.current + importProgress.jobs.current} of {importProgress.customers.total + importProgress.jobs.total} records processed
            </p>
          </div>

          {/* Customers Progress */}
          {importProgress.customers.total > 0 && (
            <div className="mb-6 bg-[var(--sf-blue-50)] border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-blue-900">Customers</span>
                <span className="text-sm font-medium text-blue-900">{Math.round(customersProgress)}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2.5 overflow-hidden mb-2">
                <div 
                  className="bg-[var(--sf-blue-500)] h-2.5 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${customersProgress}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-[var(--sf-blue-500)]">
                <span>Processed: {importProgress.customers.current} / {importProgress.customers.total}</span>
                <span className="flex gap-4">
                  <span>✓ {importProgress.customers.imported}</span>
                  <span>⊘ {importProgress.customers.skipped}</span>
                  {importProgress.customers.errors > 0 && <span className="text-red-600">✗ {importProgress.customers.errors}</span>}
                </span>
              </div>
            </div>
          )}

          {/* Jobs Progress */}
          {importProgress.jobs.total > 0 && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-green-900">Jobs</span>
                <span className="text-sm font-medium text-green-900">{Math.round(jobsProgress)}%</span>
              </div>
              <div className="w-full bg-green-200 rounded-full h-2.5 overflow-hidden mb-2">
                <div 
                  className="bg-green-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${jobsProgress}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-green-700">
                <span>Processed: {importProgress.jobs.current} / {importProgress.jobs.total}</span>
                <span className="flex gap-4">
                  <span>✓ {importProgress.jobs.imported}</span>
                  <span>⊘ {importProgress.jobs.skipped}</span>
                  {importProgress.jobs.errors > 0 && <span className="text-red-600">✗ {importProgress.jobs.errors}</span>}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Step 6: Results
  if (step === 6 && importResult) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[var(--sf-text-primary)] mb-2">
            {importType === 'customers' ? 'Customers' : 'Jobs'} Import Complete!
          </h1>
          <p className="text-[var(--sf-text-secondary)] mb-4">
            Successfully imported {importType === 'customers' ? 'customer' : 'job'} data from Booking Koala
          </p>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6 mt-6">
            <h3 className="text-lg font-semibold text-green-800 mb-4">Import Summary</h3>
            <div className="space-y-2 text-sm text-green-700">
              <p><strong>Imported:</strong> {importResult.imported} {importType === 'customers' ? 'customers' : 'jobs'}</p>
              {importResult.skipped > 0 && (
                <p><strong>Skipped:</strong> {importResult.skipped} duplicates</p>
              )}
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mt-4">
                  <p><strong>Errors:</strong> {importResult.errors.length} records failed</p>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-green-800 font-medium">View Error Details</summary>
                    <ul className="list-disc list-inside mt-2 space-y-1 text-xs">
                      {importResult.errors.slice(0, 10).map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                      {importResult.errors.length > 10 && (
                        <li>... and {importResult.errors.length - 10} more errors</li>
                      )}
                    </ul>
                  </details>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex space-x-4 justify-center">
            <button
              onClick={resetImport}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              Import More Data
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Back to Settings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BookingKoalaIntegration;


